import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/config";
import { Link } from "react-router-dom";
import { Upload, FileText, CreditCard, User, Trash2, Download, AlertCircle, CheckCircle, Bell, MapPin, Clock, Eye, Mail, Briefcase, History, IdCard, Heart, ShieldCheck, Home, Stethoscope, Accessibility, Award, Pencil, Check, X } from "lucide-react";
import DateInputFR from "@/components/DateInputFR";
import { formatSsn } from "@/components/SsnInput";
import NationalitySelect from "@/components/NationalitySelect";
import PhoneInputFr from "@/components/PhoneInputFr";
import CandidateMissionsSection from "@/components/CandidateMissionsSection";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import NotificationOptIn from "@/components/NotificationOptIn";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { JOB_PREFERENCES } from "@/constants";
import { useJobPreferences } from "@/hooks/useJobPreferences";
import axios from "axios";

// Documents accessibles aux candidats dès l'inscription (cf RGPD - finalité contractualisation)
const CANDIDATE_DOC_TYPES = [
  { key: "cv", label: "CV / Curriculum Vitae", icon: FileText, accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png" },
  { key: "id_card", label: "Pièce d'identité (CNI ou passeport)", icon: IdCard, accept: ".pdf,.jpg,.jpeg,.png" },
  { key: "carte_sejour", label: "Carte de séjour", icon: IdCard, accept: ".pdf,.jpg,.jpeg,.png" },
  { key: "attestation_secu", label: "Attestation Sécu ou carte vitale", icon: Heart, accept: ".pdf,.jpg,.jpeg,.png" },
  { key: "justificatif_domicile", label: "Justificatif de domicile", icon: Home, accept: ".pdf,.jpg,.jpeg,.png" },
  { key: "certificat_travail", label: "Certificat de travail / bulletins de salaire", icon: Briefcase, accept: ".pdf,.jpg,.jpeg,.png" },
  { key: "visite_medicale", label: "Visite médicale", icon: Stethoscope, accept: ".pdf,.jpg,.jpeg,.png" },
  { key: "rqth", label: "RQTH (travailleur handicapé)", icon: Accessibility, accept: ".pdf,.jpg,.jpeg,.png" },
  { key: "caces_habilitations", label: "CACES ou habilitations", icon: Award, accept: ".pdf,.jpg,.jpeg,.png" },
  { key: "carte_btp", label: "Carte BTP (facultatif)", icon: IdCard, accept: ".pdf,.jpg,.jpeg,.png" },
];
// Documents supplémentaires réservés aux salariés (à la signature du contrat)
const EMPLOYEE_DOC_TYPES = [
  ...CANDIDATE_DOC_TYPES,
  { key: "rib", label: "RIB / Relevé d'identité bancaire", icon: CreditCard, accept: ".pdf,.jpg,.jpeg,.png" },
];

export default function CandidateDashboard() {
  const { user, setUser } = useAuth();
  const { prefs: dynamicPrefs } = useJobPreferences();
  const JOB_PREFS = dynamicPrefs.length ? dynamicPrefs : JOB_PREFERENCES;
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [applications, setApplications] = useState([]);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    phone: user?.phone || "",
    address: user?.address || "",
    postal_code: user?.postal_code || "",
    city: user?.city || "",
    experience: user?.experience || "",
    disponibilite_debut: user?.disponibilite_debut || "",
    disponibilite_fin: user?.disponibilite_fin || "",
    preferences: user?.preferences || [],
    mobility_km: user?.mobility_km || 30,
    social_security_number: user?.social_security_number || "",
    civility: user?.civility || "",
    nationality: user?.nationality || "",
    birth_date: user?.birth_date || "",
    birth_place: user?.birth_place || "",
    birth_name: user?.birth_name || "",
    marital_status: user?.marital_status || "",
    id_doc_type: user?.id_doc_type || "",
    id_doc_validity_start: user?.id_doc_validity_start || "",
    id_doc_validity_end: user?.id_doc_validity_end || "",
    id_doc_origin: user?.id_doc_origin || "",
    id_doc_department: user?.id_doc_department || "",
    id_doc_issuer: user?.id_doc_issuer || "",
    id_doc_issuer_code: user?.id_doc_issuer_code || "",
    id_doc_number: user?.id_doc_number || "",
    iban: user?.iban || "",
    bic: user?.bic || "",
    bank_domiciliation: user?.bank_domiciliation || "",
  });

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents`, { withCredentials: true });
      setDocuments(res.data);
    } catch (err) {
      console.error("Failed to fetch documents:", err);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/applications/me`, { withCredentials: true });
      setApplications(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchApplications();
    // Tous les rôles ont désormais des documents (candidate, employee, admin)
    fetchDocuments();
  }, [user, fetchDocuments, fetchNotifications, fetchApplications]);

  // WebSocket temps réel pour recevoir les nouvelles notifications
  useEffect(() => {
    if (!user?.user_id) return;
    const wsUrl = API.replace(/^http/, "ws") + "/ws/notifications";
    // Récupérer le session_token depuis les cookies (httponly = impossible côté JS)
    // → on demande un petit endpoint de token court-terme ? Simpler : récupérer depuis /auth/me qui force le cookie,
    // puis lire le session_token via un endpoint /auth/ws-token sécurisé… ou passer par l'identifiant user_id only.
    // Ici on utilise une astuce: le WS accepte un token que l'on dérive du session cookie côté serveur via un endpoint dédié.
    let ws;
    let reconnectTimer;
    let pingTimer;
    const connect = async () => {
      try {
        const r = await axios.get(`${API}/auth/ws-token`, { withCredentials: true });
        const token = r.data.token;
        ws = new WebSocket(`${wsUrl}?token=${encodeURIComponent(token)}`);
        ws.onopen = () => {
          pingTimer = setInterval(() => { try { ws.send("ping"); } catch { /* closed */ } }, 25000);
        };
        ws.onmessage = (evt) => {
          if (evt.data === "pong") return;
          try {
            const msg = JSON.parse(evt.data);
            if (msg.type === "notification" && msg.notification) {
              setNotifications((prev) => [msg.notification, ...prev]);
              toast.message(msg.notification.title, { description: msg.notification.message });
            }
          } catch { /* ignore non-JSON */ }
        };
        ws.onclose = () => {
          clearInterval(pingTimer);
          reconnectTimer = setTimeout(connect, 5000);
        };
        ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
      } catch {
        reconnectTimer = setTimeout(connect, 10000);
      }
    };
    connect();
    return () => {
      clearInterval(pingTimer);
      clearTimeout(reconnectTimer);
      if (ws) { try { ws.close(); } catch { /* ignore */ } }
    };
  }, [user?.user_id]);

  const markAsRead = async (notifId) => {
    try {
      await axios.patch(`${API}/notifications/${notifId}/read`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => n.notif_id === notifId ? { ...n, is_read: true } : n));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await axios.patch(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch { /* ignore */ }
  };

  const handleUpload = async (docType, file) => {
    // Enforce banking details when uploading a RIB — IBAN/BIC/domiciliation must be filled.
    if (docType === "rib") {
      const iban = (user?.iban || "").trim();
      const bic = (user?.bic || "").trim();
      const dom = (user?.bank_domiciliation || "").trim();
      if (!iban || !bic || !dom) {
        toast.error("Renseignez d'abord vos coordonnées bancaires (IBAN, BIC, Domiciliation) dans « Modifier mon profil » avant de déposer votre RIB.");
        setEditingProfile(true);
        // Scroll to the bank block for convenience
        setTimeout(() => {
          document.querySelector('[data-testid="profile-iban"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
        return;
      }
    }
    setUploading((prev) => ({ ...prev, [docType]: true }));
    const formData = new FormData();
    formData.append("doc_type", docType);
    formData.append("file", file);
    try {
      await axios.post(`${API}/documents/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document depose avec succes !");
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du depot");
    } finally {
      setUploading((prev) => ({ ...prev, [docType]: false }));
    }
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, { withCredentials: true });
      toast.success("Document supprime");
      fetchDocuments();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleRename = async (docId, newName) => {
    const cleaned = (newName || "").trim();
    if (!cleaned) return;
    try {
      await axios.patch(`${API}/documents/${docId}/rename`, { name: cleaned }, { withCredentials: true });
      toast.success("Document renommé");
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de renommer");
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/download`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors du telechargement");
    }
  };

  const handleProfileSave = async () => {
    // If the user has already uploaded a RIB, banking details are mandatory.
    const hasRib = documents.some((d) => d.doc_type === "rib" && !d.is_deleted);
    if (hasRib) {
      const iban = (profileForm.iban || "").trim();
      const bic = (profileForm.bic || "").trim();
      const dom = (profileForm.bank_domiciliation || "").trim();
      if (!iban || !bic || !dom) {
        toast.error("Un RIB est déjà déposé — IBAN, BIC et Domiciliation sont obligatoires.");
        return;
      }
    }
    try {
      const res = await axios.put(`${API}/auth/profile`, profileForm, { withCredentials: true });
      setUser(res.data);
      setEditingProfile(false);
      toast.success("Profil mis a jour");
    } catch {
      toast.error("Erreur lors de la mise a jour");
    }
  };

  const getDocsByType = (type) => documents.filter((d) => d.doc_type === type);

  return (
    <main data-testid="candidate-dashboard" className="min-h-screen bg-[#FAFAFA]">
      <PWAInstallBanner testId="pwa-install-banner-dashboard" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black tracking-tight text-[#3F3F3F]">Mon espace</h1>
          <p className="text-sm text-gray-500 mt-1">
            Bienvenue, {user?.name}
          </p>
        </div>

        {/* Profile incomplete banner — OAuth users OR any user who skipped required fields.
            The banner links to /auth?complete=1 which forces the full registration form. */}
        {user?.profile_complete === false && (
          <div className="mb-6 border-l-4 border-red-500 bg-red-50 p-4 flex items-start gap-3" data-testid="profile-incomplete-banner">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-red-800">Votre inscription n&apos;est pas terminée</p>
              <p className="text-xs text-red-700 mt-1">
                Certaines informations obligatoires sont manquantes (état civil, pièce d&apos;identité, numéro de sécurité sociale…).
                Complétez votre profil pour accéder aux offres et être contacté par nos équipes.
              </p>
              <Button
                onClick={() => window.location.assign("/auth?complete=1")}
                className="mt-3 rounded-none bg-red-600 text-white hover:bg-red-700 text-xs h-8 px-4"
                data-testid="complete-profile-btn"
              >
                Compléter mon inscription
              </Button>
            </div>
          </div>
        )}

        {/* Notifications opt-in — Web Push J-1 mission reminders */}
        <div className="mb-6">
          <NotificationOptIn variant="card" testId="dashboard-notif" />
        </div>

        {/* Status Banner */}
        <div className={`p-4 mb-8 flex items-center gap-3 ${
          user?.role === "employee" ? "bg-green-50 border border-green-200" : "bg-[#E6F0F7] border border-[#0069B4]/20"
        }`}>
          {user?.role === "employee" ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-800">Statut : Salarie</p>
                <p className="text-xs text-green-600">Vous pouvez deposer vos documents ci-dessous.</p>
              </div>
            </>
          ) : (
            <>
              <AlertCircle className="w-5 h-5 text-[#0069B4] shrink-0" />
              <div>
                <p className="text-sm font-semibold text-[#004D85]">Statut : Candidat</p>
                <p className="text-xs text-[#0069B4]">Le depot de documents sera disponible lorsque votre profil sera valide par un administrateur.</p>
              </div>
            </>
          )}
          <Badge className={`ml-auto rounded-none text-xs font-semibold ${
            user?.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}>
            {user?.status === "active" ? "Actif" : "Inactif"}
          </Badge>
        </div>

        {/* Subscription Toggle */}
        <div className="bg-white border border-gray-200 p-4 mb-8 flex items-center justify-between" data-testid="subscription-section">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-[#662483]" />
            <div>
              <p className="text-sm font-semibold text-[#3F3F3F]">Recevoir les offres par email</p>
              <p className="text-xs text-gray-500">{user?.subscribed_offers ? "Vous êtes abonné(e) aux nouvelles offres" : "Vous ne recevez pas les offres par email"}</p>
            </div>
          </div>
          <Button
            variant={user?.subscribed_offers ? "default" : "outline"}
            className={`rounded-none text-sm ${user?.subscribed_offers ? "bg-[#662483] text-white hover:bg-[#4B1A61]" : "border-gray-300"}`}
            onClick={async () => {
              try {
                const res = await axios.patch(`${API}/auth/subscription`, {}, { withCredentials: true });
                setUser(res.data);
                toast.success(res.data.subscribed_offers ? "Abonnement activé" : "Abonnement désactivé");
              } catch { toast.error("Erreur"); }
            }}
            data-testid="toggle-subscription"
          >
            {user?.subscribed_offers ? "Abonné" : "S'abonner"}
          </Button>
        </div>

        {/* Notifications Section */}
        {notifications.length > 0 && (
          <div className="bg-white border border-gray-200 p-6 mb-8" data-testid="notifications-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#662483]" />
                <h2 className="text-lg font-bold text-[#3F3F3F]">Notifications</h2>
                {notifications.filter((n) => !n.is_read).length > 0 && (
                  <Badge className="bg-red-500 text-white rounded-full text-xs px-2">
                    {notifications.filter((n) => !n.is_read).length} nouvelle{notifications.filter((n) => !n.is_read).length > 1 ? "s" : ""}
                  </Badge>
                )}
              </div>
              {notifications.some((n) => !n.is_read) && (
                <Button variant="ghost" className="text-xs text-[#662483]" onClick={markAllRead} data-testid="mark-all-read">
                  Tout marquer comme lu
                </Button>
              )}
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {notifications.slice(0, 10).map((notif) => (
                <div
                  key={notif.notif_id}
                  className={`flex items-center justify-between p-3 border transition-colors cursor-pointer ${
                    notif.is_read ? "bg-white border-gray-100" : "bg-[#F0E9F3] border-[#662483]/20"
                  }`}
                  onClick={() => !notif.is_read && markAsRead(notif.notif_id)}
                  data-testid={`notification-${notif.notif_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${notif.is_read ? "bg-gray-300" : "bg-[#662483]"}`} />
                    <div className="min-w-0">
                      <p className={`text-sm truncate ${notif.is_read ? "text-gray-600" : "font-semibold text-[#3F3F3F]"}`}>
                        {notif.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{notif.message}</p>
                    </div>
                  </div>
                  {notif.offer_id && (
                    <Link
                      to="/offres"
                      className="text-xs text-[#0069B4] hover:underline shrink-0 ml-2 flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Eye className="w-3 h-3" /> Voir
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Applications History */}
        <div className="bg-white border border-gray-200 p-6 mb-8" data-testid="applications-section">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-[#662483]" />
            <h2 className="text-lg font-bold text-[#3F3F3F]">Mes candidatures</h2>
            <Badge className="bg-[#F0E9F3] text-[#662483] rounded-none font-bold ml-1">{applications.length}</Badge>
          </div>
          {applications.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">
              <Briefcase className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              Vous n'avez pas encore postulé à une offre.
              <br />
              <Link to="/offres" className="text-[#0069B4] hover:underline text-sm mt-2 inline-block">Voir les offres disponibles →</Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {applications.map((a) => {
                const statusMap = {
                  pending: { label: "En attente", cls: "bg-amber-100 text-amber-800" },
                  reviewed: { label: "Consultée", cls: "bg-blue-100 text-blue-800" },
                  accepted: { label: "Acceptée", cls: "bg-green-100 text-green-800" },
                  rejected: { label: "Refusée", cls: "bg-red-100 text-red-800" },
                };
                const s = statusMap[a.status] || statusMap.pending;
                const offerDeleted = a.offer?.status === "deleted";
                return (
                  <div key={a.application_id} data-testid={`application-${a.application_id}`} className="py-4 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-[#662483]">{a.offer?.offer_number || a.offer_number}</span>
                        <Badge className={`${s.cls} rounded-none text-[10px] font-bold uppercase tracking-wider`}>{s.label}</Badge>
                      </div>
                      <p className={`text-sm font-semibold ${offerDeleted ? "text-gray-400 italic" : "text-[#3F3F3F]"}`}>
                        {a.offer?.title || a.offer_title}
                      </p>
                      {a.offer?.location && <p className="text-xs text-gray-500 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {a.offer.location}</p>}
                      {a.comment && <p className="text-xs text-gray-600 mt-2 italic border-l-2 border-[#662483]/30 pl-2">« {a.comment} »</p>}
                      <p className="text-[11px] text-gray-400 mt-1">Postulé le {new Date(a.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</p>
                    </div>
                    {!offerDeleted && a.offer?.offer_id && (
                      <Link
                        to="/offres"
                        className="text-xs text-[#0069B4] hover:underline flex items-center gap-1 shrink-0"
                      >
                        <Eye className="w-3 h-3" /> Voir l'offre
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Mes missions (timeline read-only) */}
        <CandidateMissionsSection />

        {/* Profile Section */}
        <div className="bg-white border border-gray-200 p-6 md:p-8 mb-8" data-testid="profile-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-r from-[#662483] to-[#0069B4] flex items-center justify-center text-white text-lg font-bold">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#3F3F3F]">Mon profil</h2>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="rounded-none border-gray-300 text-sm"
              onClick={() => setEditingProfile(!editingProfile)}
              data-testid="edit-profile-btn"
            >
              {editingProfile ? "Annuler" : "Modifier"}
            </Button>
          </div>

          {editingProfile ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-1 block">Prénom</Label>
                <Input autoComplete="given-name" value={profileForm.first_name} onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })} className="rounded-none" data-testid="profile-first-name" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-1 block">Nom</Label>
                <Input autoComplete="family-name" value={profileForm.last_name} onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })} className="rounded-none" data-testid="profile-last-name" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-1 block">Téléphone</Label>
                <PhoneInputFr value={profileForm.phone} onChange={(v) => setProfileForm({ ...profileForm, phone: v })} data-testid="profile-phone" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-1 block">Adresse</Label>
                <Input value={profileForm.address} onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })} className="rounded-none" data-testid="profile-address" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-1 block">Code postal</Label>
                  <Input value={profileForm.postal_code || ""} onChange={(e) => setProfileForm({ ...profileForm, postal_code: e.target.value })} className="rounded-none" data-testid="profile-postal" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-1 block">Ville</Label>
                  <Input value={profileForm.city} onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })} className="rounded-none" data-testid="profile-city" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-1 block">Expérience</Label>
                <Input value={profileForm.experience} onChange={(e) => setProfileForm({ ...profileForm, experience: e.target.value })} className="rounded-none" placeholder="Ex: 3 ans en logistique" data-testid="profile-experience" />
              </div>
              <div className="sm:col-span-2 grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-1 block">Date de début de disponibilité *</Label>
                  <DateInputFR value={profileForm.disponibilite_debut} onChange={(v) => setProfileForm({ ...profileForm, disponibilite_debut: v })} className="rounded-none" testid="profile-dispo-debut" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-700 mb-1 block">Date de fin de disponibilité</Label>
                  <DateInputFR value={profileForm.disponibilite_fin} onChange={(v) => setProfileForm({ ...profileForm, disponibilite_fin: v })} className="rounded-none" testid="profile-dispo-fin" />
                  <p className="text-xs text-gray-400 mt-1">Facultatif</p>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Préférences de recherche</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {JOB_PREFS.map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2">
                      <Checkbox checked={(profileForm.preferences || []).includes(key)} onCheckedChange={(checked) => {
                        setProfileForm(prev => ({ ...prev, preferences: checked ? [...(prev.preferences || []), key] : (prev.preferences || []).filter(p => p !== key) }));
                      }} data-testid={`profile-pref-${key}`} />
                      <label className="text-xs text-gray-600">{label}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-sm font-semibold text-gray-700 mb-1 block flex items-center gap-2"><MapPin className="w-4 h-4" /> Mobilité géographique</Label>
                <div className="flex items-center gap-3">
                  <Input type="number" min="5" max="200" value={profileForm.mobility_km} onChange={(e) => setProfileForm({ ...profileForm, mobility_km: parseInt(e.target.value) || 30 })} className="rounded-none w-24" data-testid="profile-mobility" />
                  <span className="text-sm text-gray-500">km autour de mon adresse</span>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-sm font-semibold text-gray-700 mb-1 block flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Numéro de sécurité sociale</Label>
                <Input
                  value={profileForm.social_security_number || ""}
                  onChange={(e) => setProfileForm({ ...profileForm, social_security_number: e.target.value.replace(/[^0-9]/g, "").slice(0, 15) })}
                  placeholder="15 chiffres"
                  className="rounded-none font-mono tracking-wider"
                  inputMode="numeric"
                  autoComplete="off"
                  data-testid="profile-ssn"
                />
              </div>

              {/* État civil */}
              <div className="sm:col-span-2 border-t border-gray-100 pt-4 mt-2">
                <h3 className="text-sm font-bold text-[#662483] mb-3 uppercase tracking-wider">État civil</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Civilité</Label>
                    <Select value={profileForm.civility || ""} onValueChange={(v) => setProfileForm({ ...profileForm, civility: v })}>
                      <SelectTrigger className="rounded-none h-9" data-testid="profile-civility"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Monsieur">Monsieur</SelectItem>
                        <SelectItem value="Madame">Madame</SelectItem>
                        <SelectItem value="Mademoiselle">Mademoiselle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Situation familiale</Label>
                    <Select value={profileForm.marital_status || ""} onValueChange={(v) => setProfileForm({ ...profileForm, marital_status: v })}>
                      <SelectTrigger className="rounded-none h-9" data-testid="profile-marital"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Celibataire">Célibataire</SelectItem>
                        <SelectItem value="Marie">Marié(e)</SelectItem>
                        <SelectItem value="Pacs">Pacsé(e)</SelectItem>
                        <SelectItem value="Divorce">Divorcé(e)</SelectItem>
                        <SelectItem value="Veuf">Veuf / Veuve</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Nationalité</Label>
                    <NationalitySelect value={profileForm.nationality || ""} onChange={(v) => setProfileForm({ ...profileForm, nationality: v })} dataTestId="profile-nationality" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Nom de naissance</Label>
                    <Input value={profileForm.birth_name || ""} onChange={(e) => setProfileForm({ ...profileForm, birth_name: e.target.value })} className="rounded-none h-9" data-testid="profile-birth-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Date de naissance</Label>
                    <DateInputFR value={profileForm.birth_date || ""} onChange={(v) => setProfileForm({ ...profileForm, birth_date: v })} className="rounded-none" testid="profile-birth-date" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Lieu de naissance</Label>
                    <Input value={profileForm.birth_place || ""} onChange={(e) => setProfileForm({ ...profileForm, birth_place: e.target.value })} className="rounded-none h-9" placeholder="Ville" data-testid="profile-birth-place" />
                  </div>
                </div>
              </div>

              {/* Pièce d'identité */}
              <div className="sm:col-span-2 border-t border-gray-100 pt-4">
                <h3 className="text-sm font-bold text-[#662483] mb-3 uppercase tracking-wider">Pièce d'identité</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Type de pièce</Label>
                    <Select value={profileForm.id_doc_type || ""} onValueChange={(v) => setProfileForm({ ...profileForm, id_doc_type: v })}>
                      <SelectTrigger className="rounded-none h-9" data-testid="profile-id-type"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cni">Carte nationale d'identité</SelectItem>
                        <SelectItem value="passeport">Passeport</SelectItem>
                        <SelectItem value="carte_sejour">Carte de séjour</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Numéro de pièce</Label>
                    <Input value={profileForm.id_doc_number || ""} onChange={(e) => setProfileForm({ ...profileForm, id_doc_number: e.target.value })} className="rounded-none h-9" placeholder="ex. YJ84LQDMF" data-testid="profile-id-number" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Date de début de validité</Label>
                    <DateInputFR value={profileForm.id_doc_validity_start || ""} onChange={(v) => setProfileForm({ ...profileForm, id_doc_validity_start: v })} className="rounded-none" testid="profile-id-start" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Date de fin de validité</Label>
                    <DateInputFR value={profileForm.id_doc_validity_end || ""} onChange={(v) => setProfileForm({ ...profileForm, id_doc_validity_end: v })} className="rounded-none" testid="profile-id-end" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Pays d'émission</Label>
                    <Input value={profileForm.id_doc_origin || ""} onChange={(e) => setProfileForm({ ...profileForm, id_doc_origin: e.target.value })} className="rounded-none h-9" placeholder="ex. France" data-testid="profile-id-origin" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Département</Label>
                    <Input value={profileForm.id_doc_department || ""} onChange={(e) => setProfileForm({ ...profileForm, id_doc_department: e.target.value })} className="rounded-none h-9" placeholder="ex. 38" data-testid="profile-id-department" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Organisme émetteur</Label>
                    <Select value={profileForm.id_doc_issuer_code || ""} onValueChange={(v) => setProfileForm({ ...profileForm, id_doc_issuer_code: v })}>
                      <SelectTrigger className="rounded-none h-9" data-testid="profile-id-issuer-code"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PP">PP — Préfecture de Police (Paris)</SelectItem>
                        <SelectItem value="PR">PR — Préfecture</SelectItem>
                        <SelectItem value="SP">SP — Sous-préfecture</SelectItem>
                        <SelectItem value="MA">MA — Mairie</SelectItem>
                        <SelectItem value="CO">CO — Commissariat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Département et lieu</Label>
                    <Input value={profileForm.id_doc_issuer || ""} onChange={(e) => setProfileForm({ ...profileForm, id_doc_issuer: e.target.value })} className="rounded-none h-9" placeholder="ex. Pref Isère" data-testid="profile-id-issuer" />
                  </div>
                </div>
              </div>

              {/* Coordonnées bancaires */}
              <div className="sm:col-span-2 border-t border-gray-100 pt-4">
                <h3 className="text-sm font-bold text-[#662483] mb-3 uppercase tracking-wider">Coordonnées bancaires</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-gray-600 mb-1 block">IBAN</Label>
                    <Input value={profileForm.iban || ""} onChange={(e) => setProfileForm({ ...profileForm, iban: e.target.value.toUpperCase() })} className="rounded-none h-9 font-mono" placeholder="FR76 1234 5678 9012 3456 7890 123" data-testid="profile-iban" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">BIC</Label>
                    <Input value={profileForm.bic || ""} onChange={(e) => setProfileForm({ ...profileForm, bic: e.target.value.toUpperCase() })} className="rounded-none h-9 font-mono" placeholder="BNPAFRPPXXX" data-testid="profile-bic" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Domiciliation</Label>
                    <Input value={profileForm.bank_domiciliation || ""} onChange={(e) => setProfileForm({ ...profileForm, bank_domiciliation: e.target.value })} className="rounded-none h-9" placeholder="ex. BNP Paribas Paris 9e" data-testid="profile-bank-dom" />
                  </div>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Button
                  onClick={handleProfileSave}
                  className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold px-6 py-2 rounded-none hover:opacity-90"
                  data-testid="save-profile-btn"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><span className="font-semibold text-gray-700">Prénom :</span> <span className="text-gray-600">{user?.first_name || "-"}</span></div>
              <div><span className="font-semibold text-gray-700">Nom :</span> <span className="text-gray-600">{user?.last_name || "-"}</span></div>
              <div><span className="font-semibold text-gray-700">Email :</span> <span className="text-gray-600">{user?.email || "-"}</span></div>
              <div><span className="font-semibold text-gray-700">Téléphone :</span> <span className="text-gray-600">{user?.phone || "-"}</span></div>
              <div><span className="font-semibold text-gray-700">Adresse :</span> <span className="text-gray-600">{user?.address || "-"}, {user?.postal_code || ""} {user?.city || ""}</span></div>
              <div><span className="font-semibold text-gray-700">Mobilité :</span> <span className="text-gray-600">{user?.mobility_km || 30} km</span></div>
              <div><span className="font-semibold text-gray-700">Expérience :</span> <span className="text-gray-600">{user?.experience || "-"}</span></div>
              <div><span className="font-semibold text-gray-700">Début de disponibilité :</span> <span className="text-gray-600">{user?.disponibilite_debut || "-"}</span></div>
              <div><span className="font-semibold text-gray-700">Fin de disponibilité :</span> <span className="text-gray-600">{user?.disponibilite_fin || "Non définie"}</span></div>
              <div><span className="font-semibold text-gray-700">N° Sécu :</span> <span className="text-gray-600 font-mono">{user?.social_security_number ? formatSsn(user.social_security_number) : "Non renseigné"}</span></div>
            </div>
          )}
        </div>

        {/* Documents Section — désormais accessible à tous les rôles (RIB inclus post-inscription) */}
        <div data-testid="documents-section">
          <h2 className="text-xl font-bold text-[#3F3F3F] mb-2">Mes documents</h2>
          <p className="text-xs text-gray-500 mb-4">
            Le RIB est facultatif à l&apos;inscription mais devient <strong>obligatoire à la signature du contrat de mission</strong>.
            Si vous déposez un RIB, l&apos;IBAN, le BIC et la domiciliation bancaire doivent être renseignés dans votre profil.
          </p>
          <div className="space-y-6">
            {EMPLOYEE_DOC_TYPES.map(({ key, label, icon: Icon, accept }) => {
              const docs = getDocsByType(key);
              return (
                <div key={key} className="bg-white border border-gray-200 p-6" data-testid={`doc-section-${key}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#F0E9F3] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#662483]" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-[#3F3F3F]">{label}</h3>
                      <p className="text-xs text-gray-500">{docs.length > 0 ? `${docs.length} fichier(s) déposé(s)` : "Aucun fichier déposé"}</p>
                    </div>
                  </div>

                  {docs.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {docs.map((doc) => (
                        <DocRow
                          key={doc.doc_id}
                          doc={doc}
                          onDownload={() => handleDownload(doc.doc_id, doc.original_filename)}
                          onDelete={() => handleDelete(doc.doc_id)}
                          onRename={(newName) => handleRename(doc.doc_id, newName)}
                        />
                      ))}
                    </div>
                  )}

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="file"
                      accept={accept}
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) handleUpload(key, e.target.files[0]);
                        e.target.value = "";
                      }}
                      data-testid={`upload-input-${key}`}
                    />
                    <Button
                      variant="outline"
                      className="rounded-none border-[#662483] text-[#662483] text-sm"
                      disabled={uploading[key]}
                      asChild
                    >
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading[key] ? "Dépôt en cours..." : "Déposer un fichier"}
                      </span>
                    </Button>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </main>
  );
}

/**
 * Row displaying a single uploaded document with rename / download / delete actions.
 * The filename becomes an inline editable input on click of the pencil icon.
 * Extension is preserved (see backend PATCH /documents/{id}/rename).
 */
function DocRow({ doc, onDownload, onDelete, onRename }) {
  const original = doc.original_filename || "";
  const dotIdx = original.lastIndexOf(".");
  const initialBase = dotIdx > 0 ? original.slice(0, dotIdx) : original;
  const ext = dotIdx > 0 ? original.slice(dotIdx) : "";
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialBase);
  useEffect(() => {
    setValue(dotIdx > 0 ? original.slice(0, dotIdx) : original);
    setEditing(false);
  }, [doc.doc_id, original, dotIdx]);
  const commit = () => {
    if (value.trim() && value.trim() !== initialBase) onRename?.(value.trim());
    setEditing(false);
  };
  return (
    <div className="flex items-center justify-between bg-[#FAFAFA] p-3 border border-gray-100" data-testid={`doc-row-${doc.doc_id}`}>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <FileText className="w-4 h-4 text-gray-400 shrink-0" />
        {editing ? (
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <input
              autoFocus
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") commit(); else if (e.key === "Escape") { setValue(initialBase); setEditing(false); } }}
              className="text-sm text-[#3F3F3F] border border-gray-300 rounded-none px-2 py-1 flex-1 min-w-0 focus:outline-none focus:ring-1 focus:ring-[#662483]"
              data-testid={`rename-input-${doc.doc_id}`}
            />
            {ext && <span className="text-xs text-gray-400 select-none">{ext}</span>}
            <button onClick={commit} className="p-1 text-green-600 hover:text-green-700" title="Enregistrer" data-testid={`rename-save-${doc.doc_id}`}>
              <Check className="w-4 h-4" />
            </button>
            <button onClick={() => { setValue(initialBase); setEditing(false); }} className="p-1 text-gray-500 hover:text-gray-700" title="Annuler" data-testid={`rename-cancel-${doc.doc_id}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <span className="text-sm text-[#3F3F3F] truncate" data-testid={`doc-name-${doc.doc_id}`}>{original}</span>
            <span className="text-xs text-gray-400 shrink-0">({(doc.size / 1024).toFixed(1)} Ko)</span>
          </>
        )}
      </div>
      {!editing && (
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setEditing(true)} className="p-1 text-[#662483] hover:text-[#552070]" title="Renommer" data-testid={`rename-${doc.doc_id}`}>
            <Pencil className="w-4 h-4" />
          </button>
          <button onClick={onDownload} className="p-1 text-[#0069B4] hover:text-[#004D85]" data-testid={`download-${doc.doc_id}`}>
            <Download className="w-4 h-4" />
          </button>
          <button onClick={onDelete} className="p-1 text-red-500 hover:text-red-700" data-testid={`delete-${doc.doc_id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
